/**
 * WebsiteOrderChatPanel — embedded WhatsApp thread for a single order row.
 *
 * Mounted inside the "Website Order" tab's expanded row (BulkOrderList.js) so
 * Neera/ops can read + reply to the customer's WA conversation without
 * leaving the orders page. Reuses the exact same read/send endpoints as
 * My Chats (MyChats.js) — this is a scoped-down, single-conversation view of
 * the same underlying data, not a parallel chat system.
 *
 *   GET  /api/ai/whatsapp/conversation-by-phone?phone=       — resolve phone -> conversationId (assignedTo populated)
 *   GET  /api/ai/whatsapp/conversation/:id/messages          — thread (agentId populated)
 *   GET  /api/ai/whatsapp/conversation/:id/call-summary      — last dial/connected call for the header strip
 *   POST /api/ai/whatsapp/send                               — { conversationId, agentId, text }
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Typography, TextField, Button, CircularProgress } from "@material-ui/core";

const AI_PATH = process.env.REACT_APP_AI_PATH;
const REFRESH_MS = 15000;

const COLORS = {
  text: "#1f2937",
  muted: "#6b7280",
  wa: "#25d366",
  bubbleIn: "#f0f0f0",
  bubbleOut: "#dcf8c6",
};

// adminId is stored raw in localStorage by JWTLogin (not encrypted) — same
// pattern MyChats.js uses.
const getAdminId = () => {
  try {
    return localStorage.getItem("adminId") || null;
  } catch (e) {
    return null;
  }
};

const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

// "14 Aug 3:45pm" — used in the call-summary strip. Mirrors MyChats.js's formatCallTime.
const formatCallTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d
      .toLocaleString("en-IN", {
        day: "2-digit", month: "short",
        hour: "numeric", minute: "2-digit", hour12: true,
        timeZone: "Asia/Kolkata",
      })
      .replace(/ am/i, "am")
      .replace(/ pm/i, "pm");
  } catch {
    return "—";
  }
};

const sameDay = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};

const formatDayChip = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export default function WebsiteOrderChatPanel({ phoneNumber }) {
  const myAdminId = getAdminId();
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [callSummary, setCallSummary] = useState(null);
  const threadRef = useRef(null);

  const fetchMessages = async (conversationId) => {
    try {
      const res = await axios.get(`${AI_PATH}/api/ai/whatsapp/conversation/${conversationId}/messages`);
      setMessages(res.data?.messages || []);
    } catch (err) {
      console.error("[WebsiteOrderChatPanel] messages fetch failed:", err);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    axios
      .get(`${AI_PATH}/api/ai/whatsapp/conversation-by-phone`, { params: { phone: phoneNumber } })
      .then((res) => {
        if (cancelled) return;
        const convo = res.data?.conversation;
        if (!convo) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setConversation(convo);
        fetchMessages(convo._id).finally(() => !cancelled && setLoading(false));
        axios
          .get(`${AI_PATH}/api/ai/whatsapp/conversation/${convo._id}/call-summary`)
          .then((r) => !cancelled && setCallSummary(r.data))
          .catch((err) => console.warn("[WebsiteOrderChatPanel] call-summary failed:", err.message));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  useEffect(() => {
    if (!conversation?._id) return;
    const t = setInterval(() => fetchMessages(conversation._id), REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation]);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const sendReply = async () => {
    if (!conversation?._id || sending || !draft.trim()) return;
    const text = draft.trim();
    setSending(true);
    setDraft("");
    try {
      const res = await axios.post(`${AI_PATH}/api/ai/whatsapp/send`, {
        conversationId: conversation._id,
        agentId: myAdminId,
        text,
      });
      setMessages((prev) => [
        ...prev,
        res.data?.message || { _id: "tmp" + Date.now(), direction: "out", text, sentAt: new Date().toISOString() },
      ]);
    } catch (err) {
      alert("Send failed: " + (err.response?.data?.error || err.message));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography style={{ color: "#b71c1c", fontSize: 13 }}>Couldn't load chat: {error}</Typography>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box p={2}>
        <Typography style={{ color: COLORS.muted, fontSize: 13 }}>
          No WhatsApp conversation found for {phoneNumber}.
        </Typography>
      </Box>
    );
  }

  const thread = messages.slice().sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));

  return (
    <Box style={{ border: "1px solid #d0d0d0", borderRadius: 8, overflow: "hidden", width: "100%" }}>
      <Box style={{ background: "#075e54", color: "#fff", padding: "8px 14px" }}>
        <Typography style={{ fontSize: 13, fontWeight: 700 }}>
          {conversation.customerName || conversation.customerBusinessName || phoneNumber}
        </Typography>
        <Typography style={{ fontSize: 11, opacity: 0.85 }}>{conversation.customerPhone}</Typography>
        <Box display="flex" alignItems="center" style={{ gap: 6, marginTop: 4, flexWrap: "wrap" }}>
          {conversation.assignedTo?.name && (
            <Typography
              variant="caption"
              title="Agent currently assigned to this lead"
              style={{ color: "#fff", background: "rgba(255,255,255,0.14)", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}
            >
              👤 {conversation.assignedTo.name}
            </Typography>
          )}
          {callSummary && (callSummary.dialCount > 0 || callSummary.connectedCount > 0) && (
            <>
              <Typography
                variant="caption"
                title={`${callSummary.connectedCount} connected call(s) across all agents on this number`}
                style={{ color: "#a8e6cf", background: "rgba(255,255,255,0.14)", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}
              >
                📞 Last call: {formatCallTime(callSummary.lastConnectedAt)} ({callSummary.connectedCount})
              </Typography>
              <Typography
                variant="caption"
                title={`${callSummary.dialCount} dial attempt(s) across all agents on this number (includes unconnected)`}
                style={{ color: "#ffe082", background: "rgba(255,255,255,0.14)", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}
              >
                📱 Last dial: {formatCallTime(callSummary.lastDialAt)} ({callSummary.dialCount})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Box
        ref={threadRef}
        style={{
          background: "#e5ddd5",
          height: "70vh",
          minHeight: 420,
          overflowY: "auto",
          padding: "12px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {thread.length === 0 ? (
          <Box style={{ color: COLORS.muted, textAlign: "center", marginTop: 40, fontSize: 13 }}>No messages yet</Box>
        ) : (
          thread.flatMap((m, i) => {
            const showChip = i === 0 || !sameDay(thread[i - 1].sentAt, m.sentAt);
            const mediaSrc = m.mediaUrl
              ? m.mediaUrl.startsWith("http")
                ? m.mediaUrl
                : `${AI_PATH}/api/ai/whatsapp/media/${m.mediaUrl}`
              : null;
            return [
              showChip && (
                <Box
                  key={`d-${m._id}`}
                  alignSelf="center"
                  style={{
                    background: "#e1f3fb",
                    color: "#374151",
                    padding: "3px 10px",
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    marginTop: 4,
                  }}
                >
                  {formatDayChip(m.sentAt)}
                </Box>
              ),
              <Box
                key={m._id}
                alignSelf={m.direction === "out" ? "flex-end" : "flex-start"}
                style={{
                  background: m.direction === "out" ? COLORS.bubbleOut : COLORS.bubbleIn,
                  padding: "8px 12px",
                  borderRadius: 8,
                  maxWidth: "75%",
                  boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                }}
              >
                {m.direction === "out" && (() => {
                  const senderLabel = m.isAutoReply
                    ? "🤖 AI"
                    : (m.agentId ? (m.agentId.name || "Agent") : null);
                  return senderLabel ? (
                    <Typography style={{ fontSize: 10, fontWeight: 700, color: "#1976d2", textAlign: "right", marginBottom: 2 }}>
                      {senderLabel}
                    </Typography>
                  ) : null;
                })()}
                {m.bodyType === "image" && mediaSrc ? (
                  <img
                    src={mediaSrc}
                    alt={m.mediaCaption || "image"}
                    style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 6, display: "block", cursor: "pointer" }}
                    onClick={() => window.open(mediaSrc, "_blank")}
                  />
                ) : m.bodyType === "voice" && mediaSrc ? (
                  <audio controls src={mediaSrc} style={{ maxWidth: 220 }} />
                ) : m.bodyType === "video" && mediaSrc ? (
                  <video controls preload="none" src={mediaSrc} style={{ maxWidth: 220, borderRadius: 6 }} />
                ) : m.bodyType === "doc" && mediaSrc ? (
                  <a
                    href={mediaSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "flex", alignItems: "center", gap: 8, color: COLORS.text }}
                  >
                    📄 {m.mediaCaption || "Document"}
                  </a>
                ) : (
                  <Typography variant="body2" style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {m.text || (m.bodyType ? `[${m.bodyType}]` : "")}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  style={{ color: COLORS.muted, fontSize: 10, display: "block", textAlign: "right", marginTop: 2 }}
                >
                  {formatTime(m.sentAt)}
                </Typography>
              </Box>,
            ];
          })
        )}
      </Box>

      <Box style={{ background: "#f0f0f0", padding: "8px 12px", borderTop: "1px solid #d0d0d0", display: "flex", gap: 8 }}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Type a reply"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendReply();
            }
          }}
          disabled={sending}
          style={{ background: "#fff", borderRadius: 22 }}
          InputProps={{ style: { borderRadius: 22 } }}
        />
        <Button
          variant="contained"
          onClick={sendReply}
          disabled={!draft.trim() || sending}
          style={{ background: COLORS.wa, color: "#fff", borderRadius: 22, minWidth: 80, fontWeight: 700 }}
        >
          {sending ? "…" : "Send"}
        </Button>
      </Box>
    </Box>
  );
}
